/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit'
import { isPending, isFulfilled } from '@reduxjs/toolkit'
import { resetDashboard } from '@redux/slices/dashboard'
import { resetDeployment } from '@redux/slices/deployment'
import { resetUI } from '@redux/slices/ui'
import { C1Type } from '@redux/actions/types'
import {
  externalIDSelector,
  REGION_ACTION_TYPE,
  EXTERNAL_ID_ACTION_TYPE,
  TIME_ZONE_ACTION_TYPE,
} from '@redux/slices/common'
import api from '@redux/apis/baseApi'

const isValidRequestIdsMap: Record<string, 'valid' | 'invalid'> = {}

const resetStateMiddleware: Middleware<MiddlewareAPI, Redux.RootState> =
  store => next => action => {
    const { dispatch, getState } = store
    const state = getState()
    const externalID = externalIDSelector(state)

    if (isPending(action)) {
      isValidRequestIdsMap[action.meta.requestId] = 'valid'
    }

    if (
      action.type === C1Type.LOGGING_OUT ||
      (action.type === REGION_ACTION_TYPE &&
        action.payload !== state.common.region) ||
      (action.type === TIME_ZONE_ACTION_TYPE &&
        action.payload !== state.common.c1UserTimeZone) ||
      (action.type === EXTERNAL_ID_ACTION_TYPE && action.payload !== externalID)
    ) {
      dispatch(resetDashboard())
      dispatch(resetDeployment())
      dispatch(resetUI())
      dispatch(api.util.resetApiState())

      Object.entries(isValidRequestIdsMap).forEach(([key]) => {
        isValidRequestIdsMap[key] = 'invalid'
      })
    }

    if (isFulfilled(action)) {
      const isInvalidRequestId =
        isValidRequestIdsMap[action.meta.requestId] === 'invalid'

      delete isValidRequestIdsMap[action.meta.requestId]

      if (isInvalidRequestId) {
        return next({
          ...action,
          payload: {},
        })
      }
    }

    return next(action)
  }

export default resetStateMiddleware
